import React, { useState, useRef, useCallback, useEffect } from 'react';

// Couleurs par type de bloc
const blockColors = {
  etagere: { bg: '#e3f2fd', border: '#1565c0', label: '#1565c0' },
  comptoir: { bg: '#d7ccc8', border: '#8d6e63', label: '#5d4037' },
  frigo: { bg: '#e0f7fa', border: '#00838f', label: '#00838f' },
  vitrine: { bg: '#fff3e0', border: '#e65100', label: '#e65100' },
  mur: { bg: '#bdbdbd', border: '#616161', label: '#424242' },
  entree: { bg: '#a5d6a7', border: '#2e7d32', label: '#1b5e20' },
};

function BarLayout({ config, blocks, setBlocks, slots, onSlotClick, selectedSlotId, editMode, onDeleteBlock }) {
  const gridRef = useRef(null);
  const [dragging, setDragging] = useState(null); // { blockId, offsetX, offsetY }
  const [dragPos, setDragPos] = useState(null); // { x, y } en pixels
  const [resizing, setResizing] = useState(null); // { blockId }
  const [editingDims, setEditingDims] = useState(null); // blockId en cours d'edition dimensions
  const [editLevels, setEditLevels] = useState(1);
  const [editSlotsPerLevel, setEditSlotsPerLevel] = useState(1);
  const [editLabel, setEditLabel] = useState('');

  const cellSize = config.cellSize || 50;
  const gridCols = config.gridCols || 20;
  const gridRows = config.gridRows || 14;

  // Snap position to grid
  const snapToGrid = useCallback((px, py) => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const relX = px - rect.left;
    const relY = py - rect.top;
    return {
      x: Math.max(0, Math.min(gridCols - 1, Math.round(relX / cellSize))),
      y: Math.max(0, Math.min(gridRows - 1, Math.round(relY / cellSize))),
    };
  }, [cellSize, gridCols, gridRows]);

  // Mouse move handler
  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e) => {
      setDragPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = (e) => {
      const snapped = snapToGrid(e.clientX - dragging.offsetX, e.clientY - dragging.offsetY);
      // Clamp to grid bounds
      const block = blocks.find(b => b.id === dragging.blockId);
      if (block) {
        const maxX = gridCols - block.width;
        const maxY = gridRows - block.height;
        const newX = Math.max(0, Math.min(maxX, snapped.x));
        const newY = Math.max(0, Math.min(maxY, snapped.y));
        setBlocks(prev => prev.map(b =>
          b.id === dragging.blockId ? { ...b, x: newX, y: newY } : b
        ));
      }
      setDragging(null);
      setDragPos(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, blocks, snapToGrid, setBlocks, gridCols, gridRows]);

  // Resize handler
  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const rect = gridRef.current?.getBoundingClientRect();
      if (!rect) return;
      const block = blocks.find(b => b.id === resizing.blockId);
      if (!block) return;

      const relX = e.clientX - rect.left;
      const relY = e.clientY - rect.top;
      const newW = Math.max(1, Math.min(gridCols - block.x, Math.round((relX - block.x * cellSize) / cellSize)));
      const newH = Math.max(1, Math.min(gridRows - block.y, Math.round((relY - block.y * cellSize) / cellSize)));

      setBlocks(prev => prev.map(b =>
        b.id === resizing.blockId ? { ...b, width: newW, height: newH } : b
      ));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, blocks, setBlocks, cellSize, gridCols, gridRows]);

  const startDrag = (e, blockId) => {
    if (!editMode) return;
    e.preventDefault();
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return;
    const blockPixelX = rect.left + block.x * cellSize;
    const blockPixelY = rect.top + block.y * cellSize;
    setDragging({
      blockId,
      offsetX: e.clientX - blockPixelX,
      offsetY: e.clientY - blockPixelY,
    });
    setDragPos({ x: e.clientX, y: e.clientY });
  };

  const startResize = (e, blockId) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ blockId });
  };

  // Ouvrir l'editeur de dimensions
  const openDimsEditor = (e, block) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingDims(block.id);
    setEditLevels(block.levels || 1);
    setEditSlotsPerLevel(block.slotsPerLevel || 1);
    setEditLabel(block.label || block.type);
  };

  // Sauvegarder les dimensions
  const saveDims = (e) => {
    e.stopPropagation();
    if (editingDims) {
      setBlocks(prev => prev.map(b =>
        b.id === editingDims
          ? { ...b, levels: editLevels, slotsPerLevel: editSlotsPerLevel, label: editLabel }
          : b
      ));
    }
    setEditingDims(null);
  };

  const cancelDims = (e) => {
    e.stopPropagation();
    setEditingDims(null);
  };

  // Calculer position du bloc en drag
  const getBlockStyle = (block) => {
    const isDragging = dragging?.blockId === block.id && dragPos;
    if (isDragging) {
      const rect = gridRef.current?.getBoundingClientRect();
      if (rect) {
        return {
          left: dragPos.x - dragging.offsetX - rect.left,
          top: dragPos.y - dragging.offsetY - rect.top,
          width: block.width * cellSize,
          height: block.height * cellSize,
        };
      }
    }
    return {
      left: block.x * cellSize,
      top: block.y * cellSize,
      width: block.width * cellSize,
      height: block.height * cellSize,
    };
  };

  return (
    <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Grille */}
      <div
        ref={gridRef}
        style={{
          position: 'relative',
          width: gridCols * cellSize,
          height: gridRows * cellSize,
          background: '#fafafa',
          backgroundImage: editMode
            ? `linear-gradient(#e0e0e0 1px, transparent 1px), linear-gradient(90deg, #e0e0e0 1px, transparent 1px)`
            : 'none',
          backgroundSize: `${cellSize}px ${cellSize}px`,
          border: '2px solid #ccc',
          borderRadius: '8px',
          overflow: 'hidden',
          userSelect: 'none',
        }}
      >
        {/* Blocs places */}
        {blocks.map(block => {
          const style = getBlockStyle(block);
          const colors = blockColors[block.type] || blockColors.mur;
          const isDragging = dragging?.blockId === block.id;
          const hasSlots = block.levels > 0 && block.slotsPerLevel > 0;

          return (
            <div
              key={block.id}
              onMouseDown={(e) => startDrag(e, block.id)}
              style={{
                position: 'absolute',
                left: style.left,
                top: style.top,
                width: style.width,
                height: style.height,
                background: colors.bg,
                border: `2px solid ${colors.border}`,
                borderRadius: '6px',
                cursor: editMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
                zIndex: isDragging ? 100 : 1,
                opacity: isDragging ? 0.85 : 1,
                boxShadow: isDragging ? '0 4px 20px rgba(0,0,0,0.25)' : '0 1px 4px rgba(0,0,0,0.1)',
                transition: isDragging ? 'none' : 'box-shadow 0.2s',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Header du bloc */}
              <div style={{
                padding: '3px 6px',
                background: colors.border + '20',
                borderBottom: `1px solid ${colors.border}40`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: '22px',
                flexShrink: 0,
                gap: '4px',
              }}>
                <span style={{
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: colors.label,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                }}>
                  {block.label || block.type}
                </span>

                {/* Badge dimensions cliquable */}
                {editMode && hasSlots && (
                  <div
                    onClick={(e) => openDimsEditor(e, block)}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      background: colors.border,
                      color: '#fff',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    title="Modifier niveaux x emplacements"
                  >
                    {block.levels}x{block.slotsPerLevel}
                  </div>
                )}

                {/* Badge dimensions (non-edit) */}
                {!editMode && hasSlots && (
                  <span style={{
                    fontSize: '9px',
                    color: colors.label,
                    opacity: 0.6,
                  }}>
                    {block.levels}x{block.slotsPerLevel}
                  </span>
                )}

                {editMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteBlock(block.id); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#c62828', fontSize: '14px', padding: '0 2px',
                      lineHeight: '1', flexShrink: 0,
                    }}
                    title="Supprimer ce bloc"
                  >
                    x
                  </button>
                )}
              </div>

              {/* Popup edition dimensions */}
              {editingDims === block.id && (
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: '24px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#fff',
                    border: `2px solid ${colors.border}`,
                    borderRadius: '10px',
                    padding: '12px',
                    zIndex: 200,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                    minWidth: '200px',
                  }}
                >
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '10px' }}>
                    Configurer le bloc
                  </div>

                  {/* Nom */}
                  <div style={{ marginBottom: '8px' }}>
                    <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '3px' }}>Nom</label>
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      style={{
                        width: '100%', padding: '5px 8px', border: '1px solid #ddd',
                        borderRadius: '5px', fontSize: '12px', boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Niveaux et Slots */}
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '3px' }}>
                        Niveaux
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={editLevels}
                        onChange={(e) => setEditLevels(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                        style={{
                          width: '100%', padding: '5px 8px', border: '1px solid #ddd',
                          borderRadius: '5px', fontSize: '14px', textAlign: 'center',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'flex-end', paddingBottom: '5px',
                      fontSize: '16px', fontWeight: 'bold', color: '#999',
                    }}>
                      x
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '3px' }}>
                        Par niveau
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={editSlotsPerLevel}
                        onChange={(e) => setEditSlotsPerLevel(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                        style={{
                          width: '100%', padding: '5px 8px', border: '1px solid #ddd',
                          borderRadius: '5px', fontSize: '14px', textAlign: 'center',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>

                  {/* Apercu */}
                  <div style={{
                    background: '#f5f5f5', borderRadius: '6px', padding: '6px',
                    textAlign: 'center', marginBottom: '10px', fontSize: '12px', color: '#555',
                  }}>
                    = <b>{editLevels * editSlotsPerLevel}</b> emplacements
                  </div>

                  {/* Boutons */}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={cancelDims}
                      style={{
                        padding: '5px 14px', background: '#f5f5f5', border: '1px solid #ccc',
                        borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={saveDims}
                      style={{
                        padding: '5px 14px', background: colors.border, color: '#fff',
                        border: 'none', borderRadius: '6px', fontSize: '12px',
                        cursor: 'pointer', fontWeight: 'bold',
                      }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              )}

              {/* Contenu : slots */}
              {hasSlots && (
                <div style={{
                  flex: 1,
                  padding: '4px',
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                }}>
                  {Array.from({ length: block.levels || 0 }).map((_, level) => (
                    <div key={level} style={{
                      display: 'flex',
                      gap: '3px',
                      flexWrap: 'wrap',
                    }}>
                      {Array.from({ length: block.slotsPerLevel || 0 }).map((_, spot) => {
                        const slotId = `${block.id}-${level}-${spot}`;
                        const slotData = slots[slotId];
                        const isSelected = selectedSlotId === slotId;
                        const isEmpty = !slotData;

                        return (
                          <div
                            key={slotId}
                            onClick={(e) => { e.stopPropagation(); onSlotClick(slotId); }}
                            style={{
                              width: '62px',
                              height: '44px',
                              borderRadius: '5px',
                              border: isSelected
                                ? '2px solid #f44336'
                                : isEmpty
                                  ? '2px dashed #ccc'
                                  : `2px solid ${colors.border}`,
                              background: isEmpty ? '#ffffff90' : (slotData.product.couleur + '30'),
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '2px',
                              position: 'relative',
                              overflow: 'hidden',
                              transition: 'transform 0.1s, box-shadow 0.1s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.zIndex = '10'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.zIndex = '1'; e.currentTarget.style.boxShadow = 'none'; }}
                            title={isEmpty ? 'Vide - cliquer pour affecter' : `${slotData.product.nom} (x${slotData.quantite})`}
                          >
                            {isEmpty ? (
                              <span style={{ color: '#bbb', fontSize: '18px', fontWeight: '300' }}>+</span>
                            ) : (
                              <>
                                {/* Ligne 1 : quantite + couleur */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  marginBottom: '1px',
                                }}>
                                  <div style={{
                                    width: '10px', height: '10px', borderRadius: '3px',
                                    background: slotData.product.couleur,
                                    border: '1px solid #00000020',
                                    flexShrink: 0,
                                  }} />
                                  <div style={{
                                    background: colors.border,
                                    color: '#fff',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    borderRadius: '4px',
                                    padding: '0px 5px',
                                    minWidth: '16px',
                                    textAlign: 'center',
                                    lineHeight: '16px',
                                  }}>
                                    {slotData.quantite}
                                  </div>
                                </div>
                                {/* Ligne 2 : nom du produit */}
                                <div style={{
                                  fontSize: '9px',
                                  fontWeight: 'bold',
                                  color: '#222',
                                  lineHeight: '1.15',
                                  width: '100%',
                                  textAlign: 'center',
                                  overflow: 'hidden',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  wordBreak: 'break-word',
                                }}>
                                  {slotData.product.nom}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {/* Pas de slots : juste le type */}
              {!hasSlots && (
                <div style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '20px', opacity: 0.5,
                }}>
                  {config.blockTypes?.find(t => t.type === block.type)?.icon || ''}
                </div>
              )}

              {/* Handle de resize en mode edit */}
              {editMode && (
                <div
                  onMouseDown={(e) => startResize(e, block.id)}
                  style={{
                    position: 'absolute', bottom: 0, right: 0,
                    width: '14px', height: '14px',
                    cursor: 'nwse-resize',
                    background: `linear-gradient(135deg, transparent 50%, ${colors.border} 50%)`,
                    borderRadius: '0 0 4px 0',
                    opacity: 0.6,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BarLayout;
